import { Done , CheckCircleOutline, Cancel } from '@mui/icons-material';
import { Grid, Alert, AlertTitle } from '@mui/material';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { STRUCTURE_HELPER_TEXT } from './ClientCustomerAddEditStructure.data';
import { ClientCustomerAddEditStructureProps, SearchParams, SelectedParentType } from './ClientCustomerAddEditStructure.props';
import {
  StyledHeading,
  OuterWrapper,
  ModalBox,
  ModalContent,
  ModalFooter,
  StyledTypography,
  ContactInformationWrapper,
  LoadingWrapper
} from './ClientCustomerAddEditStructure.styles';
import {
  ActionPermissions,
  ColorProps,
  CustomPermission,
  FontStyleProps,
  NodeType,
  ParentSelectionType,
  SiblingParentDetails,
  SiteSchemaDropdown,
  SizeProps,
  StructureBasicData,
  StructureEditPermissionSet,
  StructureHelperText,
  StructureLayoutData,
  StructureTypeResponse,
  Tag,
  Warning
} from '../../../../@types';
import { PrintLabelEntityTypes } from '../../../../@types/print-label.type';
import { Box } from '../../../../components/atoms/Box';
import { Button, ButtonVariantProps } from '../../../../components/atoms/Button';
import { Checkbox, CheckboxColorProps } from '../../../../components/atoms/Checkbox';
import { CloseIcon } from '../../../../components/atoms/CloseIcon';
import { FormControlLabel } from '../../../../components/atoms/FormControlLabel';
import { Link } from '../../../../components/atoms/Link';
import { Loader, LoaderColorProps } from '../../../../components/atoms/Loader';
import { Modal } from '../../../../components/atoms/Modal';
import { PageContainer } from '../../../../components/atoms/PageContainer';
import { ToggleButtonGroup, ToogleButtonGroupColor } from '../../../../components/atoms/ToogleButtonGroup';
import { Typography, TypographyVariantProps } from '../../../../components/atoms/Typography';
import { StructureLayout } from '../../../../components/organisms/StructureLayout';
import { AutocompleteTagType, mapSavedTags } from '../../../../components/organisms/Tags';
import { TradingHourList } from '../../../../components/organisms/TradingHourList';
import { TradingHoursData } from '../../../../components/molecules/TradingHours';
import { StructureChild, ChildrenDataType } from '../../../../components/molecules/StructureChild';
import { DropdownItem, SelectMenu } from '../../../../components/atoms/SelectMenu';
import {
  useCreateStructure,
  useGetStructuresByTypeCategoryCodeAndTypeCode,
  useGetNodeChildrenByNodeType,
  useGetNodeById,
  useUpdateStructure,
  useGetNodeChildren,
  useUpdateStructureBasicData,
  useGetNodeSubTree,
  useGetSiteSchemasByClientContract,
  useGetLoggedInSiblingParentDetails,
  useGetNodeAttributesByCustomRange
} from '../../../../queries/structure-query';
import { useGetStructureTypesByCategoryCode, useGetStructureTypesByCode } from '../../../../queries/structure-type-query';
import { AddressContactType, EntityType, NodeAttributeType, PropertyType, PropertyType as PropertyTypeEnum, SpecialServiceTasksEnum, StructureTypeCategory } from '../../../../configs/enums';
import { StyledPeopleAltOutlinedIcon } from '../../../../components/atoms/ToogleButtonGroup/ToogleButtonGroup.styles';
import { Snackbar } from '../../../../components/atoms/Snackbar';
import { Switch } from '../../../../components/atoms/Switch';
import { AddressDataType, StructureAddress } from '../../../../components/organisms/StructureAddress';
import { TextField } from '../../../../components/atoms/TextField';
import { useGetAllTags } from '../../../../queries/tags-query';
import { useGetUserSystemPermissions } from '../../../../queries/user-query';
import { GRID_SPACING } from '../../../../configs/ui-constants';
import { getLocalDateString, isDropDownEmpty } from '../../../../utils/common';
import { getSortedStructureTypes, getNodeAttributes, genarateURL, getWarningsOnlyForStructures } from '../../../../utils/structure';
import { NotFoundError } from '../../../Error/NotFound';
import { isUserHasPermission } from '../../../../configs/permissions';
import { LocationMergeAccordianFragment } from '../LocationMergeAccordianFragment';
import { PartAccordianFragment } from '../PartsAccordianFragment';
import { GLOBAL_RETURNS_LOCATION, StructureTypeFields } from '../../../../constants/structureType';
import {
  ADDRESS_DEFAULT_CLIENT_CUSTOMER,
  CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER,
  HAS_STRUCTURE_EDIT_PERMISSIONS,
  MAIN_ADDRESS_CONTACT_TYPE,
  SAMPLE_EDIT_STRUCTURE_PERMISSION_SET,
  SAMPLE_STRUCTURE_LAYOUT,
  SITE_SCHEMA_SAMPLE_DATA,
  STRUCTURE_ATTRIBUTE_TYPE_FIELDS,
  TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER
} from '../../../../constants/structure';
import { ServiceAccordianFragment } from '../ServiceAccordianFragment';
import { ShipmentAccordianFragment } from '../ShipmentAccordianFragment';
import { PrintLabel } from '../../../../components/molecules/PintLabel';
import { PLATFORM_NAME } from '../../../../configs/common';
import { TabHandler } from '../../../../handlers/TabHandler';
import { ClientContractBrandPanel } from '../../../../components/molecules/ClientContractBrandPanel';
import { ChildLocationsDataType, ChildLocationsList } from '../../../../components/organisms/ChildLocationsList';
import { COLORS } from '../../../../configs/colors';

export const ClientCustomerAddEditStructure: React.FC<ClientCustomerAddEditStructureProps> = ({
  defaultClientId = '',
  defaultContractId = '',
  defaultBrandId = '',
  pageType,
  locationText,
  structureId,
  locationType,
  mainLocationNode,
  setSubLocationNode,
  setMainLocationNode,
  setLocationText,
  setOpenLocationCreateUpdatePopup,
  onSetCreatedLocation,
}) => {
  const { id: paramId } = useParams();
  const id = structureId || paramId;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get(SearchParams.ParentId);
  const parentName = searchParams.get(SearchParams.ParentName);
  const nodeTypeCode = searchParams.get(SearchParams.NodeTypeCode);
  const selectedClientId = searchParams.get(SearchParams.ClientId);
  const selectedContractId = searchParams.get(SearchParams.ContractId);
  const parentBrandId = searchParams.get(SearchParams.BrandId);
  let isNew = id === 'create';

  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [structureEditPermissions, setStructureEditPermissions] = useState<StructureEditPermissionSet>(SAMPLE_EDIT_STRUCTURE_PERMISSION_SET);
  const [siblingParentDetails, setSiblingParentDetails] = useState<SiblingParentDetails>();
  const [queryNodeType, setQueryNodeType] = useState(NodeType.Client);
  const [clientHierarchyParentId, setClientHierarchyParentId] = useState('');
  const [clients, setClients] = useState<DropdownItem[]>([]);
  const [contracts, setContracts] = useState<DropdownItem[]>([]);
  const [brands, setBrands] = useState<DropdownItem[]>([]);
  const [clientId, setClientId] = useState(defaultClientId || '');
  const [contractId, setContractId] = useState(defaultContractId || '');
  const [brandId, setBrandId] = useState(defaultBrandId || '');
  const [nodeTypes, setNodeTypes] = useState<Array<StructureTypeResponse>>([]);
  const [structureTypes, setStructureTypes] = useState<Array<StructureTypeResponse>>([]);
  const [propertyTypes, setPropertyTypes] = useState(['']);
  const [selectedParent, setSelectedParent] = useState<SelectedParentType>();
  const [selectedStructureType, setSelectedStructureType] = useState(nodeTypeCode || '-1');
  const [structureLayoutData, setStructureLayoutData] = useState<StructureLayoutData>({ ...SAMPLE_STRUCTURE_LAYOUT, name: locationText || '' });
  const [structureAddress, setStructureAddress] = useState<AddressDataType>(ADDRESS_DEFAULT_CLIENT_CUSTOMER);
  const [tradingHoursData, setTradingHoursData] = useState<TradingHoursData[]>([TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER]);
  const [childLocationsData, setChildLocationsData] = useState<ChildLocationsDataType>(CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER);
  const [selectedSystemTags, setSelectedSystemTags] = useState<(AutocompleteTagType | string)[]>([]);
  const [newCreatedTags, setNewCreatedTags] = useState<Tag[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [structureLayoutSchemaType, setStructureLayoutSchemaType] = useState<Array<SiteSchemaDropdown>>(SITE_SCHEMA_SAMPLE_DATA);
  const [parentType, setParentType] = useState<string>(parentId ? ParentSelectionType.Search : ParentSelectionType.None);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedParenHierarchy, setSelectedParenHierarchy] = useState<string | ReactNode>('');
  const [openToast, setOpenToast] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [nodeChildren, setNodeChildren] = useState<ChildrenDataType[]>([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [childNodebasicData, setChildNodebasicData] = useState<StructureBasicData>();
  const [warnings, setWarnings] = useState<Warning[]>();
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [locationName, setLocationName] = React.useState('');
  const [locationId, setLocationId] = React.useState<number>(parseInt(parentId || '0'));
  const [isSiteSchemaEnabled, setIsSiteSchemaEnabled] = useState(false);
  const [isReferenceNumbersEnabled, setIsReferenceNumbersEnabled] = useState(false);
  const [checkedGlobalReturns, setCheckedGlobalReturns] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [helperText, setHelperText] = useState<StructureHelperText>(STRUCTURE_HELPER_TEXT);
  const [validate, setValidate] = useState(false);
  const [parentSelect, setParentSelect] = useState(false);

  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const getStructureTypesByCategoryCode = useGetStructureTypesByCategoryCode(StructureTypeCategory.Operational);
  const getLoggedInSiblingParentDetailsQuery = useGetLoggedInSiblingParentDetails(true);
  const getStructuresByTypeCategoryCodeAndTypeCode = useGetStructuresByTypeCategoryCodeAndTypeCode(StructureTypeCategory.System, NodeType.Client);
  const getNodeChildrenByNodeType = useGetNodeChildrenByNodeType(clientHierarchyParentId, queryNodeType);
  const getNodeAttributesByCustomRange = useGetNodeAttributesByCustomRange(contractId, [NodeAttributeType.AdditionalConfigs]);
  const createStructure = useCreateStructure();
  const updateStructure = useUpdateStructure();
  const updateStructureBasicData = useUpdateStructureBasicData();
  const getNodeData = useGetNodeById(id || '', StructureTypeCategory.Operational);
  const getNodeChildren = useGetNodeChildren(id || '', true);
  const getNodeSubTree = useGetNodeSubTree(id || '');
  const getStructureTags = useGetAllTags(EntityType.TYPE_STRUCTURELOCATIONS);
  const getNodeSubTreeQuery = useGetNodeSubTree(locationId?.toString() || '0');
  const getStructureTypesByCode = useGetStructureTypesByCode(selectedStructureType);
  const getSiteSchemasByClientContract = useGetSiteSchemasByClientContract(clientId, contractId);

  TabHandler(isNew ? `Create Structure | ${PLATFORM_NAME}` : `Structure: ${id} | Structures | ${PLATFORM_NAME}`);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    if (permissions) {
      const hasPermissions = Object.fromEntries(
        Object.entries(HAS_STRUCTURE_EDIT_PERMISSIONS).map(([key, value]) => [
          key,
          isUserHasPermission(value, permissions),
        ])
      );
  
      setStructureEditPermissions(hasPermissions);
    }
  }, [permissions]);

  useEffect(() => {
    getLoggedInSiblingParentDetailsQuery.refetch();
    getStructureTypesByCategoryCode.refetch();
  }, []);

  useEffect(() => {
    getStructureTypesByCategoryCode.data && setNodeTypes(getStructureTypesByCategoryCode.data);
  }, [getStructureTypesByCategoryCode.data]);

  useEffect(() => {
    getLoggedInSiblingParentDetailsQuery?.data && setSiblingParentDetails(getLoggedInSiblingParentDetailsQuery?.data);
  }, [getLoggedInSiblingParentDetailsQuery.data]);

  useEffect(() => {
    if (siblingParentDetails) {
      const parentNodeHierarchy: number[] = JSON.parse(siblingParentDetails?.siblingDetails?.parentTree || '');
      if (parentNodeHierarchy?.length === 1 && siblingParentDetails?.parentDetails.nodeTypeCode === NodeType.Client) {
        setClientId(defaultClientId || parentNodeHierarchy[0]?.toString());
      } else if (parentNodeHierarchy?.length === 2 && siblingParentDetails?.parentDetails.nodeTypeCode === NodeType.Contract) {
        setClientId(defaultClientId || parentNodeHierarchy[0]?.toString());
        setContractId(defaultContractId || parentNodeHierarchy[1]?.toString());
      } else if (parentNodeHierarchy?.length === 3 && siblingParentDetails?.parentDetails.nodeTypeCode === NodeType.Brand) {
        setClientId(defaultClientId || parentNodeHierarchy[0]?.toString());
        setContractId(defaultContractId || parentNodeHierarchy[1]?.toString());
        setBrandId(defaultBrandId || parentNodeHierarchy[2]?.toString());
      } else {
        setClientId(defaultClientId || parentNodeHierarchy[0]?.toString());
        setContractId(defaultContractId || parentNodeHierarchy[1]?.toString());
        setBrandId(defaultBrandId || parentNodeHierarchy[2]?.toString());
      }
      !clients?.length && getStructuresByTypeCategoryCodeAndTypeCode.refetch();
    }
  }, [siblingParentDetails, defaultClientId, defaultContractId, defaultBrandId]);

  useEffect(() => {
    if (getStructuresByTypeCategoryCodeAndTypeCode.data) {
      setClients(getStructuresByTypeCategoryCodeAndTypeCode.data.map(item => ({ value: item.id.toString(), label: item.name, disabled: !item.isActive })));
    }
  }, [getStructuresByTypeCategoryCodeAndTypeCode.data]);

  useEffect(() => {
    if (parentId && parentName && nodeTypeCode) {
      const parentData = {
        id: parseInt(parentId),
        name: parentName,
        nodeTypeCode
      };
      setSelectedParent(parentData);
    }
  }, [parentId, parentName, nodeTypeCode]);

  useEffect(() => {
    if (clientHierarchyParentId && clientHierarchyParentId !== '0' && queryNodeType) {
      if (isNew && queryNodeType !== NodeType.Client || !isNew && !selectedBrandId || !isNew && queryNodeType === NodeType.Brand) {
        getNodeChildrenByNodeType.refetch();
      }
    }
  }, [clientHierarchyParentId, queryNodeType]);

  useEffect(() => {
    if (getNodeChildrenByNodeType.data) {
      if (queryNodeType === NodeType.Contract) {
        const contractMenuList = getNodeChildrenByNodeType.data.map(item => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive
        }));
        setContracts(contractMenuList);
        if (contractMenuList?.length > 1 && !brands?.length && contractId && contractId !== '0') {
          setClientHierarchyParentId(contractId);
          setQueryNodeType(NodeType.Brand);
        }
      }
      else if (queryNodeType === NodeType.Brand && contractId !== '0') {
        const brandMenuList = getNodeChildrenByNodeType.data.map(item => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive
        }));
        setBrands(brandMenuList);
      }
    }
  }, [getNodeChildrenByNodeType.data, queryNodeType, contractId]);

  useEffect(() => {
    contractId && getNodeAttributesByCustomRange.refetch();
  }, [contractId]);

  useEffect(() => {
    const nodeAttributeData = getNodeAttributesByCustomRange.data;
    if (nodeAttributeData && nodeAttributeData.length > 0 && nodeTypes && nodeTypes?.length > 0 && selectedParent) {
      let preferedNodeTypes: string[] = [];
      let allowedNodeTypes: string[] = [];
      if (selectedParent?.nodeTypeCode === NodeType.Brand) {
        allowedNodeTypes = nodeAttributeData[0]?.value?.serviceTasksConfigs?.find((config: any) =>
          config.code === SpecialServiceTasksEnum.StructureCreationTypes)?.brandChildren || [];
      } else {
        preferedNodeTypes = nodeTypes?.find((type) =>
          type.code === selectedParent?.nodeTypeCode)?.childNodeTypes?.map((nodeType) => nodeType.code) || [];
        allowedNodeTypes = nodeAttributeData[0]?.value?.serviceTasksConfigs?.find((config: any) =>
          config.code === SpecialServiceTasksEnum.StructureCreationTypes)?.structureTypes || [];
      }
      const allowedStructureTypes = getSortedStructureTypes(nodeTypes, preferedNodeTypes, allowedNodeTypes, selectedParent?.nodeTypeCode === NodeType.Brand);
      setStructureTypes(pageType === 'service' ? allowedStructureTypes.filter((allowedStructureType) => allowedStructureType.canHaveServices) : allowedStructureTypes);
      setSelectedStructureType(allowedStructureTypes?.length === 1 ? allowedStructureTypes[0].code : '');
      setChildLocationsData(CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER);
    } else if (!nodeAttributeData?.length) {
      setStructureTypes([]);
      setChildLocationsData(CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER);
    }
  }, [getNodeAttributesByCustomRange.data, nodeTypes, selectedParent]);

  useEffect(() => {
    if (selectedStructureType) {
      const selectedPropertyTypes = structureTypes.find(item => item.code === selectedStructureType)?.propertyTypes;

      if (selectedPropertyTypes) {
        setPropertyTypes(selectedPropertyTypes.map(property => property.propertyType));
      }
      else {
        setPropertyTypes([]);
      }
    }
  }, [structureTypes, selectedStructureType]);

  useEffect(() => {
    if (locationId) {
      getNodeSubTreeQuery.refetch();
    }
  }, [locationId]);

  useEffect(() => {
    if (getNodeSubTreeQuery.data) {
      const clientNode = getNodeSubTreeQuery.data.find(item => item.nodeTypeCode === NodeType.Client);
      if (clientNode) {
        setClients([{ value: clientNode.id.toString(), label: clientNode.name, disabled: !clientNode.isActive }]);
      }

      const contractNode = getNodeSubTreeQuery.data.find(item => item.nodeTypeCode === NodeType.Contract);

      if (contractNode) {
        setContracts([{ value: contractNode.id.toString(), label: contractNode.name, disabled: !contractNode.isActive }]);
      }

      const brandNode = getNodeSubTreeQuery.data.find(item => item.nodeTypeCode === NodeType.Brand);
      setSelectedParenHierarchy(clientNode ? ( 
        <>
          <Link href={`/configure/clients/${clientNode?.nodeTypeCode}/${clientNode?.id}`}>{clientNode?.name}</Link>/
          <Link href={`/configure/clients/${contractNode?.nodeTypeCode}/${contractNode?.id}`}>{contractNode?.name}</Link>/
          {brandNode?.name}
        </> 
      ) : '-');
      setSelectedBrandId(locationId?.toString() || '');
    }
  }, [getNodeSubTreeQuery.data]);

  useEffect(() => {
    if (!isNew) {
      setStructureAddress(ADDRESS_DEFAULT_CLIENT_CUSTOMER);
      setTradingHoursData([TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER]);
      setChildLocationsData(CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER);
      setStructureLayoutData(SAMPLE_STRUCTURE_LAYOUT);
      setCheckedGlobalReturns(false);
      setSelectedBrandId('');
      setSelectedParenHierarchy('');
      setLocationId(NaN);
      setLocationName('');

      getNodeData.refetch();
      getNodeChildren.refetch();
    }
  }, [isNew, id]);

  useEffect(() => {
    getStructureTags.data && setAllTags(getStructureTags.data);
  }, [getStructureTags.data]);

  useEffect(() => {
    if (getNodeData.data) {
      const data = getNodeData.data;
      setSelectedStructureType(data.nodeTypeCode);
      setSelectedSystemTags(mapSavedTags(data.tags));

      const relevantWarnings = getWarningsOnlyForStructures(data.warnings, true);
      setWarnings(relevantWarnings);
      setShowWarningToast(relevantWarnings.length > 0);
      setIsActive(data.isActive);

      if (data.parentNodeId) {
        getNodeSubTree.refetch();
      }

      setStructureLayoutData({
        id: data.id,
        name: data.name,
        notes: data.notes,
        selectedTags: [],
        newTags: [],
        referenceNo1: '',
        referenceNo2: '',
        referenceNo3: '',
        siteSchemaId: '0',
        zone: data.zone,
        region: data.regionType
      });

      if (propertyTypes.length) {
        setStructureAddress(ADDRESS_DEFAULT_CLIENT_CUSTOMER);
        setTradingHoursData([]);
        setCheckedGlobalReturns(false);

        data.attributes?.forEach((attribute: any) => {
          const { id, propertyTypeCode, value, updatedAt } = attribute;

          if (propertyTypeCode === PropertyTypeEnum.Address) {
            setStructureAddress({
              ...value,
              id: id,
              name: value.addressName
            });
          }
          if (propertyTypeCode === PropertyTypeEnum.TradingHours) {
            setTradingHoursData(prev => [...prev, { ...value, id: id }]);
          }
          if (propertyTypeCode === PropertyTypeEnum.ReferenceNumbers) {
            setStructureLayoutData(prev => ({ ...prev, refAttribId: id, referenceNo1: value.referenceNo1, referenceNo2: value.referenceNo2, referenceNo3: value.referenceNo3 }));
          }
          if (propertyTypeCode === PropertyTypeEnum.SiteSchema) {
            setStructureLayoutData(prev => ({ ...prev, siteSchemaAttribId: id, siteSchemaId: value.siteSchemaId }));
          }
          if (propertyTypeCode === PropertyTypeEnum.MostRecentPresentation) {
            setStructureLayoutData(prev => ({ ...prev, mostRecentPresentation: { description: value?.description, date: getLocalDateString(updatedAt) } }));
          }
          if (
            propertyTypeCode === PropertyTypeEnum.GlobalReturns &&
            isUserHasPermission(
              ActionPermissions.Configure_Structure_Type_Edit,
              permissions,
            ) &&
            propertyTypes.includes(PropertyType.GlobalReturns)
          ) {
            setCheckedGlobalReturns(value.globalReturns);
          }
        });
      }
    }

  }, [getNodeData.data, propertyTypes]);

  useEffect(() => {
    selectedStructureType && getStructureTypesByCode.refetch();
  }, [selectedStructureType]);

  useEffect(() => {
    if (pageType === 'service') {
      defaultClientId && setClientId(defaultClientId);
      defaultContractId && setContractId(defaultContractId);
      defaultBrandId && setBrandId(defaultBrandId);
    } else {
      selectedClientId && setClientId(selectedClientId);
      selectedContractId && setContractId(selectedContractId);
      parentBrandId && setBrandId(parentBrandId);
    }
  }, [selectedClientId, selectedContractId, parentBrandId, defaultClientId, defaultContractId, defaultBrandId, pageType]);

  useEffect(() => {
    if (clientId && contractId && contractId !== '0') {
      getSiteSchemasByClientContract.refetch();
    }
  }, [clientId, contractId]);

  useEffect(() => {
    getSiteSchemasByClientContract.data && setStructureLayoutSchemaType(getSiteSchemasByClientContract.data);
  }, [getSiteSchemasByClientContract.data]);

  // Use for Site schema field enable / disable logic.
  useEffect(() => {
    if (getStructureTypesByCode.data) {
      // Check selected structure type has site schema propertyType
      const propertyTypeCheck = getStructureTypesByCode.data.propertyTypes?.some(item => item.propertyType === PropertyTypeEnum.SiteSchema);
      const referenceNumbersEnabled = getStructureTypesByCode.data.propertyTypes?.some(item => item.propertyType === PropertyTypeEnum.ReferenceNumbers);
      // Check if node is under client hierarchy and client has schema records configured for enabling site schema dropdown
      if (propertyTypeCheck && parentType === NodeType.Client && getSiteSchemasByClientContract.data?.length) {
        setIsSiteSchemaEnabled(true);
        setStructureLayoutSchemaType(getSiteSchemasByClientContract.data);
      } else {
        setIsSiteSchemaEnabled(false);
        setStructureLayoutSchemaType([]);
      }
      setIsReferenceNumbersEnabled(referenceNumbersEnabled);
      !isNew && !structureTypes?.length && setStructureTypes([getStructureTypesByCode.data]);
    }
  }, [getStructureTypesByCode.data, getSiteSchemasByClientContract.data, parentType]);

  useEffect(() => {
    if (getNodeSubTree.data) {
      const parentNode = getNodeSubTree.data.find(node => node.id === Number(getNodeData?.data?.parentNodeId));
      if (parentNode?.nodeTypeCode !== NodeType.Brand) {
        getNodeData.data?.clientId && setClientId(defaultClientId || getNodeData.data.clientId.toString());
        getNodeData.data?.contractId && setContractId(defaultContractId || getNodeData.data.contractId.toString());
        parentNode && setSelectedBrandId(defaultBrandId || parentNode.id.toString());
        setSelectedParenHierarchy(<><Link href={`/structure/${parentNode?.id}`}>{parentNode?.name}</Link></> || '');
      }
      else {
        setSelectedBrandId(defaultBrandId || getNodeData?.data?.parentNodeId || '');
        const clientNode = getNodeSubTree.data.find(item => item.nodeTypeCode === NodeType.Client);

        if (clientNode) {
          setClientId(defaultClientId || clientNode.id.toString());
          setClients([{ value: clientNode.id.toString(), label: clientNode.name, disabled: !clientNode.isActive }]);
        }

        const contractNode = getNodeSubTree.data.find(item => item.nodeTypeCode === NodeType.Contract);

        if (contractNode) {
          setContractId(defaultContractId || contractNode.id.toString());
          setContracts([{ value: contractNode.id.toString(), label: contractNode.name, disabled: !contractNode.isActive }]);
        }

        const brandNode = getNodeSubTree.data.find(item => item.nodeTypeCode === NodeType.Brand);
        setSelectedParenHierarchy(
          <>
            <Link href={`/configure/clients/${clientNode?.nodeTypeCode}/${clientNode?.id}`}>{clientNode?.name}</Link>/
            <Link href={`/configure/clients/${contractNode?.nodeTypeCode}/${contractNode?.id}`}>{contractNode?.name}</Link>/
            {brandNode?.name}
          </>
        );
        setParentType(ParentSelectionType.Client);
      }
    }

  }, [getNodeSubTree.data, getNodeData.data, defaultClientId, defaultContractId, defaultBrandId]);

  useEffect(() => {
    if (getNodeChildren.data) {
      setNodeChildren(getNodeChildren.data.map(item => ({ id: item.id, name: item.name, type: item.nodeTypeCode, isActive: item.isActive, typeName: item.nodeType.name })));
    }
  }, [getNodeChildren.data]);

  useEffect(() => {
    structureLayoutData.selectedTags = [...selectedSystemTags];
    structureLayoutData.newTags = [...newCreatedTags];
  }, [selectedSystemTags, newCreatedTags]);

  useEffect(() => {
    if (childLocationsData?.childrenLocations?.length > 0) {
      setDisableSave(childLocationsData?.childrenLocations?.some((location) => location?.isDuplicate));
    } else {
      disableSave && setDisableSave(false);
    }
  }, [childLocationsData]);

  const onChangeNodeType = (nodeType: NodeType, parentId: string) => {
    if (nodeType === NodeType.Contract) {
      setClientId(parentId);
    } else {
      setContractId(parentId);
    }

    setClientHierarchyParentId(parentId);
    setQueryNodeType(nodeType);
  };

  const onChangeStructureType = (selectedStructureType: string) => {
    setSelectedStructureType(selectedStructureType);
    setIsReferenceNumbersEnabled(false);
    setIsSiteSchemaEnabled(false);
    setStructureLayoutData({ ...SAMPLE_STRUCTURE_LAYOUT, name: locationText || '' });
    setNewCreatedTags([]);
    setSelectedSystemTags([]);
    setStructureAddress(ADDRESS_DEFAULT_CLIENT_CUSTOMER);
    setTradingHoursData([TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER]);
    setChildLocationsData(CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER);
  };

  const getChildLocationsStructureTypesData = () => {
    const nodeAttributeData = getNodeAttributesByCustomRange.data;
    if (nodeAttributeData && nodeAttributeData.length > 0 && nodeTypes && nodeTypes?.length > 0 && selectedStructureType) {
      const childNodeTypes: string[] = nodeTypes?.find((type) =>
        type.code === selectedStructureType)?.childNodeTypes?.map((nodeType) => nodeType.code) || [];
      const allowedNodeTypes: string[] = nodeAttributeData[0]?.value?.serviceTasksConfigs?.find((config: any) =>
        config.code === SpecialServiceTasksEnum.StructureCreationTypes)?.structureTypes || [];

      const allowedStructureTypes = getSortedStructureTypes(nodeTypes, childNodeTypes, allowedNodeTypes);
      const childStructureTypes = allowedStructureTypes?.map((structureType) => {
        return {
          label: structureType.name,
          value: structureType.code,
          hasReferenceNumbers: nodeTypes?.find((type) =>
            type.code === structureType.code)?.propertyTypes?.some((property) =>
            property.propertyType === PropertyTypeEnum.ReferenceNumbers)
        };
      });
      return childStructureTypes;
    } else {
      return [];
    }
  };

  const onCancel = () => {
    if (isNew) {
      if (!parentId && [NodeType.Client, NodeType.Contract].includes(siblingParentDetails?.parentDetails?.nodeTypeCode as NodeType)) {
        setSelectedParent(undefined);
        setBrandId('0');
        siblingParentDetails?.parentDetails?.nodeTypeCode === NodeType.Client && setContractId('0');
        siblingParentDetails?.parentDetails?.nodeTypeCode === NodeType.Client && brands?.length > 0 && setBrands([]);
        setQueryNodeType(siblingParentDetails?.parentDetails?.nodeTypeCode === NodeType.Client ? NodeType.Contract : NodeType.Brand);
        setClientHierarchyParentId(`${siblingParentDetails?.parentDetails?.id}`);
      } else if (![NodeType.Client, NodeType.Contract].includes(siblingParentDetails?.parentDetails?.nodeTypeCode as NodeType)) {
        setSelectedParent({
          id: parseInt(brandId),
          name: brands.find((brand) => brand.value === brandId)?.label || '',
          nodeTypeCode: NodeType.Brand
        });
      }
      setSelectedStructureType('-1');
      setStructureLayoutData(SAMPLE_STRUCTURE_LAYOUT);
      setNewCreatedTags([]);
      setSelectedSystemTags([]);
      setIsReferenceNumbersEnabled(false);
      setIsSiteSchemaEnabled(false);
      setStructureAddress(ADDRESS_DEFAULT_CLIENT_CUSTOMER);
      setTradingHoursData([TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER]);
      setChildLocationsData(CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER);
    } else {
      setStructureLayoutData({
        ...structureLayoutData,
        name: getNodeData.data?.name || '',
        notes: getNodeData.data?.notes,
        selectedTags: [],
        newTags: []
      });
      setSelectedSystemTags(mapSavedTags(getNodeData.data?.tags || []));

      const referenceNumbers = getNodeData.data?.attributes?.find((attribute: any) => attribute.propertyTypeCode === PropertyTypeEnum.ReferenceNumbers);
      setStructureLayoutData(prev => ({
        ...prev,
        referenceNo1: referenceNumbers?.value?.referenceNo1,
        referenceNo2: referenceNumbers?.value?.referenceNo2,
        referenceNo3: referenceNumbers?.value?.referenceNo3
      }));

      const address = getNodeData.data?.attributes?.find((attribute: any) => attribute.propertyTypeCode === PropertyTypeEnum.Address);
      setStructureAddress({
        ...address?.value,
        name: address?.value?.addressName
      });

      const tradingHours = getNodeData.data?.attributes?.filter((attribute: any) => attribute.propertyTypeCode === PropertyTypeEnum.TradingHours)?.map((tradingHour) => tradingHour?.value);
      setTradingHoursData(tradingHours || []);
    }
    setHelperText(STRUCTURE_HELPER_TEXT);
    setValidate(false);
    if (pageType === 'service') {
      setOpenLocationCreateUpdatePopup && setOpenLocationCreateUpdatePopup(false);
      return;
    }
  };

  const isValid = () => {
    const hasEmptyChildLocations = childLocationsData?.selectedStructureType &&
        childLocationsData?.childrenLocations?.some((node) => !node.name);
    const hasMandatoryAddressFields = 
    [PropertyTypeEnum.Address,PropertyTypeEnum.Contact].every((type) => propertyTypes.includes(type)) ?
      structureAddress?.name && structureAddress?.contactNumber && structureAddress?.contactCompany &&
        structureAddress?.line1 && structureAddress?.suburb && structureAddress?.postCode &&
        structureAddress?.stateId && structureAddress?.countryId : true;

    setHelperText({ 
      ...helperText,
      nameHelperText: !structureLayoutData.name ? 'Name is required' : '',
      childNodeNameHelperText: hasEmptyChildLocations ? 'Node name is required' : '',
      addressDataHelperText: !hasMandatoryAddressFields ? 'Mandatory address fields are required' : ''
    });

    return !isDropDownEmpty(selectedStructureType.toString()) && !!structureLayoutData.name && hasMandatoryAddressFields && !hasEmptyChildLocations;
  };

  const onHandleSave = async () => {
    if (!isValid()) {
      setValidate(true);
      return;
    }
    setDisableSave(true);

    const defaultContact = {
      contactType: AddressContactType.Main,
      contactName: structureAddress?.name,
      organization: structureAddress?.contactCompany,
      phone1: structureAddress?.contactNumber,
      phone2: '',
      email: structureAddress?.email,
      notes: '',
      isActive: true
    };

    const attributes = getNodeAttributes(
      [structureAddress!],
      propertyTypes,
      [defaultContact],
      tradingHoursData,
      [],
      structureLayoutData,
      checkedGlobalReturns,
      isSiteSchemaEnabled
    );
    
    if (isNew) {
      const newNode: any = await createStructure.mutateAsync({
        ...structureLayoutData,
        code: structureLayoutData.name,
        isActive: true,
        parentNodeId: selectedParent?.id,
        nodeTypeCode: selectedStructureType,
        attributes: attributes,
        childLocationsData: childLocationsData !== CHILD_LOCATION_DEFAULT_CLIENT_CUSTOMER ? childLocationsData : []
      }).catch((error) => error && setDisableSave(false));

      if (newNode?.isAllowedAtRoot === false) {
        setShowWarningToast(true);
        setWarnings([{
          field: 'Note Type',
          message: 'Parent is not provided, Check if Node Type allowed to be at Root'
        }]);
        setDisableSave(false);
        return;
      } else {
        setWarnings([]);
        setOpenToast(true);

        // empty states to avoid duplicate displaying of records
        setStructureAddress(ADDRESS_DEFAULT_CLIENT_CUSTOMER);
        setSelectedBrandId('');

        if (pageType === 'service') {
          onSetCreatedLocation && onSetCreatedLocation(newNode.id);
          setMainLocationNode && mainLocationNode && setMainLocationNode({ ...mainLocationNode, id: newNode.id });
          setOpenLocationCreateUpdatePopup && setOpenLocationCreateUpdatePopup(false);
          return;
        }

        setTimeout(() => {
          const url = `/structure/${newNode.id}`;
          navigate(url, { replace: true });
          setDisableSave(false);
        }, 2000);
      }
    } else {
      await updateStructure.mutateAsync({
        ...structureLayoutData,
        id: id,
        code: structureLayoutData.name,
        isActive: isActive,
        parentNodeId: getNodeData.data?.parentNodeId,
        attributes: attributes,
        page: 'structure'
      });

      if (pageType === 'service' && locationText) {
        if (locationType === 'main-location' && setLocationText) {
          setLocationText(structureLayoutData.name);

          if (!isActive) {
            setLocationText('');
          }
        } else if (locationType === 'sub-location' && setLocationText) {
          const mainLocationNameText = locationText.split('/')[0];
          const updatedLocationText = mainLocationNameText + '/' + structureLayoutData.name;
          setLocationText(updatedLocationText);

          if (!isActive && setSubLocationNode) {
            setSubLocationNode(undefined);
            setLocationText(mainLocationNameText);
          }
        }
      }

      setDisableSave(false);
      setOpenToast(true);
    }
    await getNodeData.refetch();
    setParentSelect(false);

    if (pageType === 'service' && !isNew) {
      setOpenLocationCreateUpdatePopup && setOpenLocationCreateUpdatePopup(false);
      return;
    }
  };

  const handleIsActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const onAddNewChildStructure = (selectedNodeTypeCode?: string, nodeId?: string) => {
    if (nodeId) {
      const selectedNode = nodeChildren.find(item => item.id === +nodeId);

      if (selectedNode) {
        setChildNodebasicData({ id: selectedNode.id, name: selectedNode.name, isActive: selectedNode.isActive });
        setShowEditPopup(true);
      }
    }
    else {
      let clientNodeId = clientId?.toString();

      if (!clientNodeId) {
        clientNodeId = getNodeData.data?.clientId?.toString() || '';
      }

      let brandNodeId = brandId?.toString();

      if (!brandNodeId) {
        brandNodeId = getNodeData.data?.brandId?.toString() || '';
      }

      const url = genarateURL(location.pathname, id, structureLayoutData.name, selectedNodeTypeCode, selectedStructureType, structureTypes, clientNodeId, contractId, brandNodeId);
      window.open(url, '_self');
    }
  };

  const onChildLocationsChange = (childLocationsData: ChildLocationsDataType) => {
    const updatedChildLocationsData = childLocationsData?.childrenLocations?.map((location) => ({
      ...location,
      isDuplicate: childLocationsData?.childrenLocations?.filter((location2) => location2.name === location.name).length > 1
    }));
    setChildLocationsData({ selectedStructureType: childLocationsData.selectedStructureType, childrenLocations: updatedChildLocationsData });
    if (helperText?.childNodeNameHelperText && !childLocationsData?.childrenLocations?.some((node) => !node.name)) {
      setHelperText({ ...helperText, childNodeNameHelperText: '' });
    }
  };

  const onChangeGlobalReturns = (event: ChangeEvent<HTMLInputElement>) => setCheckedGlobalReturns(event.target.checked);
  
  const onChangeChildNodeData = async () => {
    if (!childNodebasicData) return;
    await updateStructureBasicData.mutateAsync(childNodebasicData);
    setNodeChildren(prev => {
      const node = prev.find(item => item.id === childNodebasicData?.id);

      if (node) {
        node.name = childNodebasicData?.name || node.name;
        node.isActive = childNodebasicData?.isActive || node.isActive;
      }

      return prev;
    });
    setShowEditPopup(false);
    setOpenToast(true);
  };

  const onChangeActiveFlag = async (isActive: boolean, id: string) => {
    const selectedNode = nodeChildren.find(item => item.id === +id);

    if (selectedNode) {
      await updateStructureBasicData.mutateAsync({
        ...selectedNode,
        isActive: isActive
      });

      setNodeChildren(prev => {
        const node = prev.find(item => item.id === +id);

        if (node) {
          node.isActive = isActive;
        }

        return prev;
      });
    }
  };

  const getLocationSearchParentNodeId = () => {
    return ![NodeType.Client, NodeType.Contract, NodeType.Brand].includes(siblingParentDetails?.parentDetails?.nodeTypeCode as NodeType) ?
      siblingParentDetails?.parentDetails?.id : undefined;
  };

  if (id === 'create' && isUserHasPermission(ActionPermissions.Structure_Create_General, permissions)) {
    isNew = true;
  } else if (getNodeData.data && isUserHasPermission(ActionPermissions.Structure_Edit_View_Structure_Location, permissions)) {
    isNew = false;
  } 
  
  if (id !== 'create' && !getNodeData.data) {
    return getNodeData.isFetching ? <LoadingWrapper><Loader color={LoaderColorProps.Success} /></LoadingWrapper> : <NotFoundError></NotFoundError>;
  }

  return (
    <PageContainer>
      <StyledHeading variant={TypographyVariantProps.H5} fontWeight={600}>
        {`${isNew ? 'Create' : 'Update'} Structure Location`}
      </StyledHeading>
      <OuterWrapper>
        <Grid container spacing={GRID_SPACING}>
          {(isNew || !getNodeData?.data?.parentNodeId) &&
            <>
              <ClientContractBrandPanel
                isNew={isNew}
                disableClient
                onChangeNodeType={onChangeNodeType}
                clients={clients}
                contracts={contracts}
                brands={brands}
                clientId={parseInt(clientId)}
                contractId={parseInt(contractId)}
                brandId={parseInt(brandId)}
                disableContract={contracts?.length === 1 || siblingParentDetails?.parentDetails?.nodeTypeCode !== NodeType.Client}
                disableBrand={brands?.length === 1 || ![NodeType.Client, NodeType.Contract].includes(siblingParentDetails?.parentDetails?.nodeTypeCode as NodeType)} 
                onParentSelect={(nodeId, nodeName, nodeTypeCode, parentTree, isFromLiveSearch) => {
                  if (nodeId === 0) {
                    setSelectedParent(undefined);
                    nodeTypeCode === NodeType.Contract && setBrandId('0');
                  } else {
                    (contractId === '' || contractId === '0') && parentTree && parentTree?.length >= 2 && setContractId(parentTree[1].toString());
                    (brandId === '' || brandId === '0') && parentTree && parentTree?.length >= 3 && setBrandId(parentTree[2].toString());
                    const parentData = {
                      id: nodeId,
                      name: nodeName,
                      nodeTypeCode,
                      isFromLiveSearch
                    };
                    !parentId && setSelectedParent(parentData);
                    nodeTypeCode === NodeType.Brand && setBrandId(nodeId.toString());
                  }
                  setSelectedStructureType('-1');
                  setIsReferenceNumbersEnabled(false);
                  setIsSiteSchemaEnabled(false);
                }}
                locationSearchEnabled
                locationData={selectedParent?.nodeTypeCode !== NodeType.Brand ? selectedParent : undefined}
                locationSearchParentNodeId={getLocationSearchParentNodeId()}
                parentNodeId={parentId || ''}
              />
              <Grid mb={1} display={isNew ? '' : 'none'} size={{ xs: 4 }}>
                <SelectMenu
                  id="type-selection"
                  label="Structure Type"
                  selectedValue={selectedStructureType}
                  required
                  items={structureTypes.map(item => {
                    return { value: item.code, label: item.name };
                  })}
                  disabled={!isNew || !selectedParent}
                  validate={validate}
                  optionalLabelEnabled
                  onChange={(structureType) => onChangeStructureType(structureType)}
                />
              </Grid>
            </>
          }
          <Grid container justifyContent="space-between" mx={3} width="100%">
            <Grid size={{ xs: 4 }}>
              {!isNew && (selectedBrandId !== null && selectedBrandId !== '' || getNodeData?.data?.parentNodeId != null) && selectedParenHierarchy !== '-' && !parentSelect &&
                <>
                  <Typography variant={TypographyVariantProps.H6}>
                    Parent
                  </Typography>
                  <Grid mb={2} size={10}>
                    <ToggleButtonGroup
                      size={SizeProps.Small}
                      color={ToogleButtonGroupColor.Standard}
                      value={parentType}
                      exclusive={true}
                      activeTab={ParentSelectionType.Client}
                      items={[
                        {
                          value: ParentSelectionType.Client,
                          label: parentType === ParentSelectionType.Client && selectedBrandId !== null && selectedBrandId !== '' ? 'Client' : 'Structure',
                          subLabel:  locationId && locationName ? <><Link href={`/structure/${locationId}`}>{locationName}</Link></> : selectedParenHierarchy,
                          icon: <StyledPeopleAltOutlinedIcon />
                        }
                      ]}
                    />
                  </Grid>
                </>
              }
              {!isNew &&
                <Grid mb={1} size={{ xs: 12 }}>
                  <SelectMenu
                    id="type-selection"
                    label="Structure Type"
                    disabled
                    selectedValue={selectedStructureType}
                    items={structureTypes.map(item => {
                      return { value: item.code, label: item.name };
                    })}
                    onChange={(value) => { setSelectedStructureType(value); }}
                  />
                </Grid>
              }
              <StructureLayout
                isNew={isNew}
                helperText={helperText}
                setHelperText={setHelperText}
                structureLayoutData={structureLayoutData}
                structureLayoutSchemaType={structureLayoutSchemaType}
                onChange={(structureData: StructureLayoutData) => {
                  setStructureLayoutData(structureData);
                  if(structureData.name){
                    selectedStructureType && setDisableSave(false);
                  }
                }}
                allTags={allTags}
                renderNotes={true}
                enableSiteSchemaType={isSiteSchemaEnabled}
                enableReferenceNumbers={isReferenceNumbersEnabled}
                onChangeSelectedTags={(value) => {
                  setSelectedSystemTags([...value]);
                }}
                onChangeNewTags={(value) => {
                  setNewCreatedTags([...value]);
                }}
                assignedSelectedTags={[...selectedSystemTags]}
                assignedNewTags={[...newCreatedTags]}
                structureEditPermissions={structureEditPermissions}
                isClientCustomerStructureCreation
                selectedStructureType={selectedStructureType}
              />
            </Grid>
            <Grid size={{ xs: 8 }}>
              {!isNew &&
                <>
                  <Grid container ml={2} mb={2} mt={2}>
                    <Grid mr={1}>
                      <FormControlLabel
                        control={
                          <Switch onChange={handleIsActiveChange}
                            defaultChecked
                            disabled={!structureEditPermissions?.hasActiveInactiveEditPermission}
                            checked={isActive}
                            color={ColorProps.Primary}
                          />
                        }
                        label="Active"
                      />
                    </Grid>
                  </Grid>
                  {StructureTypeFields.filter(type => !STRUCTURE_ATTRIBUTE_TYPE_FIELDS.includes(type.key)).map((item, index) => (
                    <Grid key={index} container ml={2} mb={1}>
                      <Grid mr={1}>
                        {propertyTypes.includes(item.key) &&
                          <CheckCircleOutline fontSize="small" color="primary" />
                        }
                        {!propertyTypes.includes(item.key) &&
                          <Cancel fontSize="small" color="warning" />
                        }
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        {item.value}
                      </Grid>
                    </Grid>
                  ))}
                  <Grid direction="column" container ml={2} mb={1}>
                    {propertyTypes.includes(PropertyType.GlobalReturns) && isUserHasPermission(ActionPermissions.Configure_Structure_Type_Edit, permissions)
                      &&
                      <Grid mr={1}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={checkedGlobalReturns}
                              onChange={onChangeGlobalReturns}
                              color={CheckboxColorProps.Primary}
                              disabled={!structureEditPermissions.hasGlobalReturnEditPermission}
                            />
                          }
                          label={GLOBAL_RETURNS_LOCATION}
                        />
                      </Grid>
                    }
                  </Grid>
                  <Grid container ml={2} mb={1} mt={2}>
                    <Grid>
                      <PrintLabel entityId={Number(id) || 0} entityType={PrintLabelEntityTypes.Structure}/>
                    </Grid>
                  </Grid>
                </>
              }
              {[PropertyTypeEnum.Address,PropertyTypeEnum.Contact].every((type) => propertyTypes.includes(type)) ?
                <ContactInformationWrapper>
                  <StyledTypography variant={TypographyVariantProps.H6}>Contact Information</StyledTypography>
                  <StructureAddress
                    switchLabel="Manual"
                    addressData={structureAddress}
                    onChange={(updatedAddressData) => { 
                      setStructureAddress(updatedAddressData);
                      if (
                        helperText?.addressDataHelperText && updatedAddressData?.name &&
                        updatedAddressData?.contactNumber && updatedAddressData?.contactCompany &&
                        updatedAddressData?.line1 && updatedAddressData?.suburb && updatedAddressData?.postCode &&
                        updatedAddressData?.stateId && updatedAddressData?.countryId
                      ) {
                        setHelperText({ ...helperText, addressDataHelperText: '' });
                      }
                    }}
                    addressTypeData={[MAIN_ADDRESS_CONTACT_TYPE]}
                    hasAddressEditPermission={!isNew ? structureEditPermissions?.hasContractAddressEditPermission : true}
                    enableContactNumber
                    enableAddressContactFields
                    isOneAddress
                    disableTypeSelection
                    isClientCustomerStructureCreation
                    onUseLocationName={() => {
                      const updatedAddressData: AddressDataType = {
                        ...structureAddress,
                        contactCompany: structureLayoutData?.name
                      };
                      setStructureAddress(updatedAddressData);
                    }}
                    error={helperText?.addressDataHelperText}
                  />
                </ContactInformationWrapper>
                :
                selectedParent && selectedStructureType !== '' && selectedStructureType !== '-1' &&
                <StyledTypography fontStyle={FontStyleProps.Italic} variant={TypographyVariantProps.Body1} textColor={COLORS.Gray} sx={{ marginBottom: '20px' }}>
                  {`Structure type ${structureTypes.find((type) => type.code === selectedStructureType)?.name || ''} does not allow Contact Information`}
                </StyledTypography>
              }
              {propertyTypes.includes(PropertyTypeEnum.TradingHours) ?
                <TradingHourList
                  heading="Trading Hours"
                  tradingHourValue={tradingHoursData}
                  onTradingHoursChange={(value) => setTradingHoursData(value)}
                  onTradingHoursCreate={(value) => setTradingHoursData(value)}
                  onTradingHoursDelete={(index) => {
                    const updatedData = [...tradingHoursData];
                    if (index > -1) {
                      updatedData.splice(index, 1);
                    }
                    setTradingHoursData([...updatedData]);
                  }}
                  hasTradingHoursEditPermission={!isNew ? structureEditPermissions?.hasTradingHoursEditPermission : true}
                  isClientCustomerStructureCreation
                />
                :
                selectedParent && selectedStructureType !== '' && selectedStructureType !== '-1' &&
                <StyledTypography fontStyle={FontStyleProps.Italic} variant={TypographyVariantProps.Body1} textColor={COLORS.Gray} sx={{ marginBottom: '20px' }}>
                  {`Structure type ${structureTypes.find((type) => type.code === selectedStructureType)?.name || ''} does not allow Trading Hours`}
                </StyledTypography>
              }
              {isNew && getNodeAttributesByCustomRange.data && getNodeAttributesByCustomRange.data[0]?.value?.serviceTasksConfigs?.some((config: any) =>
                config.code === SpecialServiceTasksEnum.CreateChildrenOnCreateStructureLocation) &&
                <ChildLocationsList
                  heading="Child Locations"
                  structureTypes={getChildLocationsStructureTypesData()}
                  childLocationsData={childLocationsData}
                  disableInputs={!selectedStructureType || !selectedParent}
                  onChildLocationChange={(value) => onChildLocationsChange(value)}
                  onChildLocationCreate={(value) => onChildLocationsChange(value)}
                  onChildLocationDelete={(index) => {
                    const updatedData = [...childLocationsData.childrenLocations];
                    if (index > -1) {
                      updatedData.splice(index, 1);
                    }
                    onChildLocationsChange({ ...childLocationsData, childrenLocations: [...updatedData] });
                  }}
                  onSmartInput={(bulkChildLocations) => onChildLocationsChange(bulkChildLocations)}
                  error={helperText?.childNodeNameHelperText}
                />
              }
            </Grid>
            <Grid container justifyContent="flex-end" spacing={2} mr={3} mb={4} width="100%">
              <Grid>
                <Button variant={ButtonVariantProps.Secondary} onClick={onCancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid>
                <Button disabled={disableSave} variant={ButtonVariantProps.Primary} onClick={onHandleSave}>
                  Save
                </Button>
              </Grid>
            </Grid>
            {!isNew && structureEditPermissions?.hasViewChildrenEditPermission &&
              <Grid container spacing={GRID_SPACING} width="100%">
                <Grid size={{ xs: 12 }}>
                  <StructureChild
                    editLabel="Edit"
                    switchLabel="Active"
                    childrenData={nodeChildren}
                    hasViewChildrenEditPermission={structureEditPermissions?.hasViewChildrenEditPermission}
                    onClick={onAddNewChildStructure}
                    onChangeActiveFlag={onChangeActiveFlag}
                    parentStructureType={selectedStructureType}
                    isClientCustomerStructureCreation
                  />
                </Grid>
              </Grid>
            } {!isNew && id && propertyTypes.includes(PropertyType.Parts) && structureEditPermissions?.hasViewPartsEditPermission &&
              <Grid container marginTop={2} width="100%">
                <Grid size={{ xs: 12 }}>
                  <PartAccordianFragment
                    nodeId={parseInt(id)}
                  />
                </Grid>
              </Grid>
            } {!isNew && id && (propertyTypes.includes(PropertyType.Service) || propertyTypes.includes(PropertyType.ServicesAssignment)) && structureEditPermissions?.hasViewServicesEditPermission &&
              <Grid container marginTop={2} width="100%">
                <Grid size={{ xs: 12 }}>
                  <ServiceAccordianFragment nodeId={parseInt(id)} permissions={permissions} />
                </Grid>
              </Grid>
            } {!isNew && id && structureEditPermissions?.hasViewShipmentsEditPermission &&
              <Grid container marginTop={2} width="100%">
                <Grid size={{ xs: 12 }}>
                  <ShipmentAccordianFragment
                    nodeId={parseInt(id)}
                  />
                </Grid>
              </Grid>
            }
            {!isNew && id && structureEditPermissions?.hasAllowMergeEditPermission &&
              <Grid container marginTop={2} width="100%">
                <Grid size={{ xs: 12 }}>
                  <LocationMergeAccordianFragment mergeNodeId={parseInt(id)} nodeTypeCode={selectedStructureType} />
                </Grid>
              </Grid>
            }
          </Grid>
        </Grid>
      </OuterWrapper>
      <Modal
        open={showEditPopup}
        onClose={() => setShowEditPopup(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ModalBox>
          <CloseIcon handleClose={() => setShowEditPopup(false)} />
          <ModalContent>
            <TextField label="Name" value={childNodebasicData?.name} onChange={(value: string) => {
              setChildNodebasicData(prev => {
                if (prev) {
                  return { ...prev, name: value };
                }
              });
            }}></TextField>
          </ModalContent>
          <ModalFooter>
            <Button variant={ButtonVariantProps.Primary} startIcon={<Done />} onClick={onChangeChildNodeData}>
              Save
            </Button>
          </ModalFooter>
        </ModalBox>
      </Modal>
      <Snackbar
        open={openToast}
        autoHideDuration={2000}
        message="Successfully Saved"
        onClose={() => { setOpenToast(false); }}
      />
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right', }} open={showWarningToast} autoHideDuration={8000} onClose={() => { setShowWarningToast(false); }}>
        <Alert onClose={() => { setShowWarningToast(false); }} severity="warning">
          <AlertTitle>Warnings</AlertTitle>
          {warnings && warnings.map((warning, index) => <Box key={index}>{warning.message}</Box>)}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};